import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ChatroomData,
  MessageData,
  getChatroomMessages,
  sendChatroomMessage,
  markChatroomSeen,
} from "./utilities";
import { ChatBubble } from "./ChatBubble";
import { useFadfaddahSocket } from "./useFadfaddahSocket";

interface ChatroomViewProps {
  chatroom: ChatroomData;
}

function getUserId(): string | null {
  return localStorage.getItem("id")
    || localStorage.getItem("userId")
    || localStorage.getItem("userID");
}

export function ChatroomView({ chatroom }: ChatroomViewProps) {
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    try {
      const data = await getChatroomMessages(chatroom.id);
      setMessages(data);
      await markChatroomSeen(chatroom.id);
    } catch (err) {
      console.error("Failed to fetch messages", err);
    } finally {
      setLoading(false);
    }
  }, [chatroom.id]);

  // Listen for new messages via socket — instantly refresh when a message arrives
  useFadfaddahSocket(getUserId(), chatroom.id, fetchMessages);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const sent = await sendChatroomMessage(chatroom.id, text);
      setMessages((prev) => [...prev, sent]);
      setInput("");
    } catch (err) {
      console.error("Failed to send message", err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return <div className="fadfaddah-room-loading">Loading messages…</div>;
  }

  return (
    <div className="fadfaddah-room">
      <div className="fadfaddah-room-header">
        <span>Chat with {chatroom.patient?.name || "Patient"}</span>
      </div>
      <div className="fadfaddah-room-messages">
        {messages.length === 0 ? (
          <div className="fadfaddah-room-empty">
            No messages yet. Send a message to start.
          </div>
        ) : (
          messages.map((msg) => (
            <ChatBubble
              key={msg.id}
              text={msg.text}
              senderRole={msg.senderRole}
              createdAt={msg.createdAt}
              seen={msg.seen}
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>
      <div className="fadfaddah-room-input">
        <textarea
          className="fadfaddah-room-textarea"
          placeholder="Type a message…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button
          className="fadfaddah-room-send"
          onClick={handleSend}
          disabled={!input.trim() || sending}
        >
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}
